import { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle, ChevronRight } from "lucide-react";
import LeadForm from "@/forms/engine/LeadForm";
import quizPaymentFinder, { paymentFinderContext } from "@/forms/definitions/quizPaymentFinder";
import { useToolLifecycle } from "@/utils/trackingHooks";

const PSF_ITEM_NAME = "payment_solution_finder";
const PSF_ITEM_OPTS = { itemType: "finder" };

const QUESTIONS = [
  {
    id: "businessType",
    label: "What best describes your business?",
    options: [
      { value: "retail", label: "Retail shop" },
      { value: "hospitality", label: "Hospitality / restaurant / bar" },
      { value: "service", label: "Service business" },
      { value: "ecommerce", label: "E-commerce / online store" },
      { value: "developer", label: "Web developer / agency" },
      { value: "other", label: "Other" },
    ],
  },
  {
    id: "paymentMethod",
    label: "How do you mainly take payments?",
    options: [
      { value: "in_person", label: "In person" },
      { value: "online", label: "Online" },
      { value: "both", label: "Both" },
      { value: "exploring", label: "I'm just exploring options" },
    ],
  },
  {
    id: "primaryInterest",
    label: "What are you most interested in?",
    options: [
      { value: "lower_costs", label: "Lower card processing costs" },
      { value: "card_machines", label: "Better card machines" },
      { value: "epos", label: "EPOS system" },
      { value: "gateway", label: "Online payment gateway" },
      { value: "payment_links", label: "Payment links / remote payments" },
      { value: "partner", label: "Earning commission as a partner" },
    ],
  },
  {
    id: "locations",
    label: "How many locations do you operate?",
    options: [
      { value: "1", label: "1" },
      { value: "2-3", label: "2\u20133" },
      { value: "4-10", label: "4\u201310" },
      { value: "10+", label: "10+" },
    ],
  },
  {
    id: "turnover",
    label: "What is your approximate monthly card turnover?",
    options: [
      { value: "under_10k", label: "Under \u00a310,000" },
      { value: "10k_30k", label: "\u00a310,000\u2013\u00a330,000" },
      { value: "30k_75k", label: "\u00a330,000\u2013\u00a375,000" },
      { value: "75k_plus", label: "\u00a375,000+" },
    ],
  },
  {
    id: "integrations",
    label: "Do you need any integrations?",
    options: [
      { value: "no", label: "No" },
      { value: "woocommerce", label: "Website / WooCommerce" },
      { value: "epos", label: "Till / EPOS" },
      { value: "booking", label: "Booking system" },
      { value: "not_sure", label: "Not sure" },
    ],
  },
  {
    id: "nextStep",
    label: "What would you like next?",
    options: [
      { value: "savings_estimate", label: "Instant savings estimate" },
      { value: "product_rec", label: "Product recommendation" },
      { value: "speak", label: "Speak to someone" },
      { value: "partner_info", label: "Partner information" },
    ],
  },
];

const SCORING_RULES = {
  businessType: {
    retail: { card_machines: 3, epos_solution: 2 },
    hospitality: { epos_solution: 4, card_machines: 1 },
    service: { card_machines: 2, savings_review: 2 },
    ecommerce: { gateway_solution: 4, woocommerce_solution: 2 },
    developer: { partner_programme: 5, woocommerce_solution: 2 },
    other: { savings_review: 1 },
  },
  paymentMethod: {
    in_person: { card_machines: 3, epos_solution: 2 },
    online: { gateway_solution: 4, woocommerce_solution: 2 },
    both: { gateway_solution: 2, epos_solution: 2, multi_site_solution: 1 },
    exploring: { savings_review: 2 },
  },
  primaryInterest: {
    lower_costs: { savings_review: 5 },
    card_machines: { card_machines: 5 },
    epos: { epos_solution: 5 },
    gateway: { gateway_solution: 5 },
    payment_links: { gateway_solution: 3 },
    partner: { partner_programme: 6 },
  },
  locations: {
    "1": { savings_review: 1 },
    "2-3": { multi_site_solution: 2, epos_solution: 1 },
    "4-10": { multi_site_solution: 4, gateway_solution: 1 },
    "10+": { multi_site_solution: 6 },
  },
  turnover: {
    under_10k: { savings_review: 1 },
    "10k_30k": { card_machines: 2, savings_review: 2 },
    "30k_75k": { epos_solution: 2, gateway_solution: 2 },
    "75k_plus": { multi_site_solution: 4, gateway_solution: 2 },
  },
  integrations: {
    no: { card_machines: 1 },
    woocommerce: { woocommerce_solution: 5, gateway_solution: 2 },
    epos: { epos_solution: 5 },
    booking: { gateway_solution: 3 },
    not_sure: { savings_review: 1 },
  },
  nextStep: {
    savings_estimate: { savings_review: 4 },
    product_rec: { epos_solution: 2, gateway_solution: 2, card_machines: 2 },
    speak: { savings_review: 1 },
    partner_info: { partner_programme: 5 },
  },
};

const TIE_BREAK_ORDER = [
  "partner_programme",
  "woocommerce_solution",
  "gateway_solution",
  "multi_site_solution",
  "epos_solution",
  "card_machines",
  "savings_review",
];

const RESULTS = {
  savings_review: {
    headline: "Card Payment Savings Review",
    description: "Based on your answers, a savings review looks like the best next step. We can help you compare your current setup and identify where you may be overpaying.",
    benefits: ["Understand your current payment costs", "Compare your options clearly", "See whether switching could save you money"],
    primaryCta: "Get my savings review",
    primaryUrl: "/switch-and-save",
    secondaryCta: "Book a quick call",
    secondaryUrl: "/book",
  },
  epos_solution: {
    headline: "EPOS & Payments Recommendation",
    description: "Based on your answers, an EPOS-led setup looks like the best fit for your business. This could help combine payments, till functionality, and operational efficiency.",
    benefits: ["Combine till and card payments", "Improve day-to-day operations", "Get a setup suited to your sector"],
    primaryCta: "Get my recommendation",
    primaryUrl: "/epos-systems",
    secondaryCta: "Book a demo",
    secondaryUrl: "/book",
  },
  card_machines: {
    headline: "Card Machine Recommendation",
    description: "Based on your answers, a card machine solution looks like the best fit. We can help you choose the right devices and pricing for your business.",
    benefits: ["Choose the right terminal setup", "Improve customer payment experience", "Find a cost-effective solution"],
    primaryCta: "Find my card machine",
    primaryUrl: "/card-machines",
    secondaryCta: "Speak to the team",
    secondaryUrl: "/contact",
  },
  gateway_solution: {
    headline: "Online Payments / Gateway Recommendation",
    description: "Based on your answers, an online payments solution looks like the right fit. We can help with gateway setup, online payments, and integration options.",
    benefits: ["Accept payments online with confidence", "Explore gateway and integration options", "Support your online sales growth"],
    primaryCta: "Explore online payments",
    primaryUrl: "/payment-gateway",
    secondaryCta: "Speak to the team",
    secondaryUrl: "/contact",
  },
  woocommerce_solution: {
    headline: "WooCommerce Payments Recommendation",
    description: "Based on your answers, a WooCommerce-focused payment setup looks like the best fit. We can help with integration, gateway setup, and partner support.",
    benefits: ["Connect payments into WooCommerce", "Improve checkout experience", "Get support for implementation"],
    primaryCta: "Explore WooCommerce payments",
    primaryUrl: "/partners/integrations/woocommerce",
    secondaryCta: "Speak to the team",
    secondaryUrl: "/contact",
  },
  multi_site_solution: {
    headline: "Multi-site Payment Solution",
    description: "Based on your answers, you may need a more tailored multi-location solution. We can help design the right setup across sites, teams, and payment channels.",
    benefits: ["Support multiple sites efficiently", "Improve consistency across locations", "Tailor payments to operational needs"],
    primaryCta: "Get a tailored solution",
    primaryUrl: "/contact",
    secondaryCta: "Book a consultation",
    secondaryUrl: "/book",
  },
  partner_programme: {
    headline: "Partner Programme Recommendation",
    description: "Based on your answers, the CanDo Payments partner programme looks like the best fit. This could help you create recurring income from businesses you already work with.",
    benefits: ["Earn recurring commission", "Add value for your clients", "Access support and partnership options"],
    primaryCta: "Become a partner",
    primaryUrl: "/partner-programme",
    secondaryCta: "Book a partner call",
    secondaryUrl: "/book",
  },
};

function computeResult(answers) {
  const scores = {};
  TIE_BREAK_ORDER.forEach((cat) => { scores[cat] = 0; });

  QUESTIONS.forEach((q) => {
    const answer = answers[q.id];
    if (!answer) return;
    const rules = SCORING_RULES[q.id]?.[answer];
    if (!rules) return;
    Object.entries(rules).forEach(([cat, pts]) => {
      scores[cat] = (scores[cat] || 0) + pts;
    });
  });

  let maxScore = 0;
  let winner = "savings_review";
  TIE_BREAK_ORDER.forEach((cat) => {
    if (scores[cat] > maxScore) {
      maxScore = scores[cat];
      winner = cat;
    }
  });

  return { category: winner, scores };
}

function ProgressBar({ current, total }) {
  const pct = ((current + 1) / total) * 100;
  return (
    <div className="w-full" data-testid="quiz-progress">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-brand-grey">{`Question ${current + 1} of ${total}`}</span>
        <span className="text-xs font-bold text-brand-aqua">{`${Math.round(pct)}%`}</span>
      </div>
      <div className="h-1.5 bg-brand-light-grey rounded-full overflow-hidden">
        <div className="h-full bg-brand-aqua rounded-full transition-all duration-500 ease-out" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function QuestionStep({ question, selected, onSelect }) {
  return (
    <div data-testid={`quiz-question-${question.id}`}>
      <h3 className="font-heading text-xl sm:text-2xl font-bold text-brand-black mb-6">{question.label}</h3>
      <div className="space-y-2.5">
        {question.options.map((opt) => {
          const active = selected === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onSelect(opt.value)}
              className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all flex items-center justify-between group ${active ? "border-brand-aqua bg-brand-aqua-light" : "border-brand-light-grey bg-white hover:border-brand-aqua/40 hover:bg-brand-aqua-light/20"}`}
              data-testid={`quiz-option-${opt.value}`}
              aria-pressed={active}
            >
              <span className={`text-sm font-semibold ${active ? "text-brand-aqua-dark" : "text-brand-black"}`}>{opt.label}</span>
              {active ? (
                <CheckCircle size={18} className="text-brand-aqua flex-shrink-0" />
              ) : (
                <ChevronRight size={16} className="text-brand-light-grey group-hover:text-brand-aqua/50 flex-shrink-0 transition-colors" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultCtaButtons({ data }) {
  const isExternal = data.secondaryUrl.startsWith("http");
  const SecondaryTag = isExternal ? "a" : Link;
  const secondaryProps = isExternal
    ? { href: data.secondaryUrl, target: "_blank", rel: "noopener noreferrer" }
    : { to: data.secondaryUrl };

  return (
    <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
      <Link to={data.primaryUrl} className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine" data-testid="quiz-primary-cta">
        {data.primaryCta}
        <ArrowRight size={16} />
      </Link>
      <SecondaryTag {...secondaryProps} className="bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-8 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-brand-aqua-light transition-colors" data-testid="quiz-secondary-cta">
        {data.secondaryCta}
      </SecondaryTag>
    </div>
  );
}

function ResultScreen({ result }) {
  const data = RESULTS[result.category];

  return (
    <div className="text-center" data-testid="quiz-result">
      <div className="w-16 h-16 rounded-2xl bg-brand-aqua flex items-center justify-center mx-auto mb-5">
        <CheckCircle size={28} className="text-white" />
      </div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-2">Your recommendation</p>
      <h3 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black mb-4" data-testid="quiz-result-headline">{data.headline}</h3>
      <p className="text-sm text-brand-grey leading-relaxed max-w-lg mx-auto mb-6">{data.description}</p>
      <div className="space-y-2.5 max-w-sm mx-auto mb-8">
        {data.benefits.map((b) => (
          <div key={b} className="flex items-center gap-2.5 text-left">
            <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />
            <span className="text-sm text-brand-black">{b}</span>
          </div>
        ))}
      </div>
      <ResultCtaButtons data={data} />
    </div>
  );
}

function LeadFormSuccess() {
  return (
    <div className="text-center py-6" data-testid="quiz-form-success">
      <CheckCircle size={32} className="text-brand-aqua mx-auto mb-3" />
      <h4 className="font-heading text-lg font-bold text-brand-black mb-1">Thank you</h4>
      <p className="text-sm text-brand-grey">We&apos;ll be in touch with your personalised recommendation shortly.</p>
    </div>
  );
}

function useQuizNavigation() {
  const [step, setStep] = useState(0);
  const [showResult, setShowResult] = useState(false);

  const handleBack = useCallback(() => {
    if (showResult) { setShowResult(false); return; }
    if (step > 0) setStep((s) => s - 1);
  }, [showResult, step]);

  const canGoBack = step > 0 || showResult;

  return { step, setStep, showResult, setShowResult, handleBack, canGoBack };
}

function useQuizState() {
  const nav = useQuizNavigation();
  const [answers, setAnswers] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { markStarted, markCompleted } = useToolLifecycle(PSF_ITEM_NAME, PSF_ITEM_OPTS);

  const currentQuestion = QUESTIONS[nav.step];
  const currentAnswer = answers[currentQuestion?.id];
  const totalSteps = QUESTIONS.length;

  const result = useMemo(() => {
    if (!nav.showResult) return null;
    return computeResult(answers);
  }, [nav.showResult, answers]);

  const leadContext = useMemo(
    () => (result ? paymentFinderContext(answers, QUESTIONS, result, RESULTS[result.category].headline) : {}),
    [answers, result]
  );

  useEffect(() => { if (result) markCompleted(); }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback((value) => {
    markStarted();
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
  }, [currentQuestion, setAnswers]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = useCallback(() => {
    if (!currentAnswer) return;
    if (nav.step < totalSteps - 1) {
      nav.setStep((s) => s + 1);
    } else {
      nav.setShowResult(true);
    }
  }, [currentAnswer, nav, totalSteps]);

  const handleRestart = useCallback(() => {
    nav.setStep(0);
    nav.setShowResult(false);
    setAnswers({});
    setFormSubmitted(false);
  }, [nav, setAnswers, setFormSubmitted]);

  const handleFormSubmit = useCallback(() => {
    setFormSubmitted(true);
  }, [setFormSubmitted]);

  return {
    ...nav, answers, formSubmitted, leadContext, currentQuestion, currentAnswer,
    totalSteps, result, handleSelect, handleNext, handleRestart, handleFormSubmit,
  };
}

function QuizBody({ state }) {
  const { step, showResult, currentQuestion, currentAnswer, totalSteps, result, canGoBack, leadContext, formSubmitted, handleSelect, handleNext, handleBack, handleRestart, handleFormSubmit } = state;

  if (showResult && result) {
    return (
      <>
        <ResultScreen result={result} />
        <div className="border-t border-brand-light-grey/60 pt-6 mt-6">
          <h4 className="font-heading text-lg font-bold text-brand-black text-center mb-1">Get your personalised recommendation</h4>
          <p className="text-xs text-brand-grey text-center mb-4">Tell us where to send your tailored follow-up.</p>
          {formSubmitted ? (
            <LeadFormSuccess />
          ) : (
            <LeadForm
              schema={quizPaymentFinder}
              context={leadContext}
              onSuccess={handleFormSubmit}
              variant="compact"
            />
          )}
        </div>
        <div className="text-center mt-6">
          <button onClick={handleRestart} className="text-xs text-brand-grey hover:text-brand-aqua transition-colors underline underline-offset-2" data-testid="quiz-restart">Start again</button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-6"><ProgressBar current={step} total={totalSteps} /></div>
      <QuestionStep question={currentQuestion} selected={currentAnswer} onSelect={handleSelect} />
      <div className="flex items-center justify-between mt-8">
        <button onClick={handleBack} disabled={!canGoBack} className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${canGoBack ? "text-brand-grey hover:text-brand-black" : "text-transparent cursor-default"}`} data-testid="quiz-back">
          <ArrowLeft size={14} /> Back
        </button>
        <button onClick={handleNext} disabled={!currentAnswer} className={`flex items-center gap-2 font-semibold rounded-full px-6 py-2.5 text-sm btn-form-ready ${currentAnswer ? "is-ready" : ""} text-white`} data-testid="quiz-next">
          {step === totalSteps - 1 ? "See my result" : "Next"}
          <ArrowRight size={14} />
        </button>
      </div>
    </>
  );
}

export default function PaymentSolutionFinder({ className = "" }) {
  const state = useQuizState();

  return (
    <div className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`} data-testid="payment-solution-finder">
      <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 border-b border-brand-aqua/10">
        <h3 className="font-heading text-lg font-bold text-brand-black">Payment Solution Finder</h3>
        <p className="text-xs text-brand-grey mt-0.5">Answer a few quick questions to find the right solution for your business</p>
      </div>

      <div className="p-6 sm:p-8">
        <QuizBody state={state} />
      </div>
    </div>
  );
}
